import { IBeneficiarie } from '@smiles/voebiz-core-beneficiaries/dist/Types/BeneficiariesApi.types';
import React, { FC, createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { getBeneficiariesList } from 'services/services';
import { capitalize } from 'utils/capitalize';

const exportDefault = {
  exportCsv: () => undefined,
  downloading: false,
};

export interface ContextExportCsv {
  exportCsv: () => void;
  downloading: boolean;
}

const ExportCsvContext = createContext<Partial<ContextExportCsv>>(exportDefault);

const ExportCsvProvider: FC<{ children: JSX.Element }> = ({ children }) => {
  const { t } = useTranslation(['exportList']);

  const [downloading, setDownloading] = useState(false);

  const [beneficiaries, setBeneficiaries] = useState<IBeneficiarie[]>([]);

  const csvLinkRef = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null);

  const headers = useMemo(
    () => [
      { label: t('name'), key: 'name' },
      { label: t('cpf'), key: 'cpf' },
      { label: t('email'), key: 'email' },
      { label: t('birthdate'), key: 'birthdate' },
      { label: t('created'), key: 'created' },
      { label: t('status'), key: 'status' },
    ],
    [t],
  );

  const formatBeneficiaries = useCallback(
    (items: IBeneficiarie[]) =>
      items.flatMap((item) => {
        return {
          ...item,
          name: capitalize(item.name),
          status: item.status === 'Active' ? t('active') : t('inactive'),
        };
      }),
    [t],
  );

  const getBeneficiaries = useCallback(async (): Promise<IBeneficiarie[]> => {
    let allBeneficiaries: IBeneficiarie[] = [];
    let page = 1;
    const pageSize = 99;
    let totalPages = 1;

    while (page <= totalPages) {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await getBeneficiariesList({ page, pageSize });
      const response = data as {
        beneficiaryList: IBeneficiarie[];
        lastPage: boolean;
        totalPages: number;
      };

      if (page === 1 && response.totalPages) {
        totalPages = response.totalPages;
      }

      allBeneficiaries = allBeneficiaries.concat(response.beneficiaryList);
      page += 1;
    }

    const list = formatBeneficiaries(allBeneficiaries);
    setBeneficiaries(list);
    return allBeneficiaries;
  }, [formatBeneficiaries]);

  const exportCsv = useCallback(async () => {
    setDownloading(true);

    await getBeneficiaries();

    setDownloading(false);

    csvLinkRef?.current.link.click();
  }, [getBeneficiaries]);

  const value = useMemo(
    () => ({
      exportCsv,
      downloading,
    }),
    [exportCsv, downloading],
  );

  const getFileName = () => {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const date = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}`;
    const time = `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
    return `dados_equipe_${date}_${time}.csv`;
  };

  return (
    <ExportCsvContext.Provider value={value}>
      <CSVLink headers={headers} filename={getFileName()} data={beneficiaries} ref={csvLinkRef} />
      {children}
    </ExportCsvContext.Provider>
  );
};

const useExportCsv = () => {
  const beneficiaries = useContext(ExportCsvContext);

  if (!beneficiaries) {
    throw new Error('Cannot use `useExportCsv` outside of ExportCsvProvider');
  }
  return beneficiaries;
};

export { ExportCsvContext, ExportCsvProvider, useExportCsv };
